import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { bi, setBi } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, Mail, AlertTriangle, Clock, FileCheck2, Info } from "lucide-react";

const UI = {
  tr: { intro: "Hedef ülkenizi ve giriş yılınızı seçin; resmî başvuru tarihleri otomatik listelenir. Üniversitenizin özel tarihlerini de ekleyebilirsiniz. 30/14/7 gün kala uygulama içinde ve e-posta ile hatırlatılır.", country: "Hedef Ülke / Sistem", year: "Giriş Yılı", pick: "Ülke seçin", none: "Önce ülke ve yıl seçin.", approx: "yaklaşık", daysLeft: "gün kaldı", today: "Bugün!", passed: "geçti", custom: "Özel Tarih Ekle", customTitle: "Başlık (örn. Portfolyo teslimi)", customNote: "Not (isteğe bağlı)", add: "Ekle", email: "E-posta hatırlatması al (30/14/7 gün kala)", test: "Şimdi hatırlatma e-postası gönder", sent: "E-posta gönderildi:", official: "Resmî tarih", mine: "Özel", need: "Başlık ve tarih gerekli", warn: "Resmî tarihler genel takvimdir; üniversitenizin kendi son tarihlerini mutlaka kontrol edin.",
    docTitle: "Belgelerin Planlanan Teslim Tarihleri",
    docIntro: "Ülke seçiminden bağımsız olarak, aşağıdaki belgeler için planladığınız teslim tarihini girin. Planlanan tarih, belgenin teslim edildiği anlamına gelmez; teslim ettiğinizde ayrıca işaretleyin.",
    planned: "Planlanan teslim tarihi", notPlanned: "Henüz planlanmadı", submitted: "Teslim edildi", actualDate: "Gerçek teslim tarihi",
    requiredQ: "Bölüm için gerekli mi?", reqYes: "Evet", reqNo: "Hayır", reqPick: "Seçin",
    examName: "Sınav adı / adları ve sonuçları", portfolioInfo: "Portfolyo bilgisi / bağlantısı",
    docBadge: "Belge", statusSubmitted: "Teslim edildi", statusPlanned: "Planlandı",
    docInfo: "Belgeler için kesin son teslim tarihleri danışmanınız tarafından başvuracağınız kurum ve programa göre bildirilecektir. Genel olarak, güncel ve resmî belgelerin en geç Ekim - Kasım ayında teslim edilmesi önerilir. Bu öneri, kurumların resmî başvuru ve belge son tarihleri yerine geçmez." },
  en: { intro: "Choose your destination country and entry year; official application deadlines are listed automatically. Add your university's own dates too. You'll be reminded in-app and by email 30/14/7 days before.", country: "Destination / System", year: "Entry Year", pick: "Select country", none: "Select a country and year first.", approx: "approx.", daysLeft: "days left", today: "Today!", passed: "passed", custom: "Add Custom Deadline", customTitle: "Title (e.g. Portfolio submission)", customNote: "Note (optional)", add: "Add", email: "Email reminders (30/14/7 days before)", test: "Send reminder email now", sent: "Email sent to:", official: "Official", mine: "Custom", need: "Title and date required", warn: "Official dates are general; always check your university's own deadlines.",
    docTitle: "Planned Document Submission Dates",
    docIntro: "Independently of your country choice, enter the date you plan to submit each document below. A planned date does not mean the document has been submitted; tick it separately once you submit.",
    planned: "Planned submission date", notPlanned: "Not planned yet", submitted: "Submitted", actualDate: "Actual submission date",
    requiredQ: "Required for the programme?", reqYes: "Yes", reqNo: "No", reqPick: "Select",
    examName: "Exam name(s) and results", portfolioInfo: "Portfolio info / link",
    docBadge: "Document", statusSubmitted: "Submitted", statusPlanned: "Planned",
    docInfo: "The exact final submission deadlines for documents will be communicated by your advisor according to the institution and programme you apply to. In general, up-to-date and official documents should be submitted by October–November at the latest. This recommendation does not replace institutions' official application and document deadlines." },
};

const YEARS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() + i);

const DOC_LABELS = {
  transcript: { tr: "Güncel transkript", en: "Current transcript" },
  statement: { tr: "Motivasyon / niyet mektubu", en: "Motivation / personal statement" },
  references: { tr: "Referans mektubu / mektupları", en: "Reference letter(s)" },
  languageTest: { tr: "IELTS / TOEFL dil yeterlilik sonucu", en: "IELTS / TOEFL language result" },
  academicExams: { tr: "Akademik sınav sonuçları", en: "Academic exam results" },
  portfolio: { tr: "Portfolyo", en: "Portfolio" },
};
const FIXED_DOCS = ["transcript", "statement", "references", "languageTest"];
const COND_DOCS = [{ key: "academicExams", textField: "name" }, { key: "portfolio", textField: "info" }];

function daysUntil(iso) { return Math.ceil((new Date(iso) - new Date().setHours(0, 0, 0, 0)) / 86400000); }

export const DeadlinesStep = ({ cvId, cv, lang, onChange }) => {
  const u = UI[lang];
  const d = cv.deadlines || {};
  const [countries, setCountries] = useState([]);
  const [system, setSystem] = useState([]);
  const [draft, setDraft] = useState({ title: "", date: "", note: "" });
  const [sending, setSending] = useState(false);
  const set = (patch) => onChange({ ...d, custom: d.custom || [], emailReminders: d.emailReminders !== false, ...patch });

  useEffect(() => { api.get("/deadlines/countries").then((r) => setCountries(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!d.country || !d.year) { setSystem([]); return; }
    api.get(`/deadlines/system?country=${d.country}&year=${d.year}`).then((r) => setSystem(r.data)).catch(() => setSystem([]));
  }, [d.country, d.year]);

  const docs = d.documents || {};
  const setDoc = (key, patch) => set({ documents: { ...docs, [key]: { ...(docs[key] || {}), ...patch } } });

  const docItems = [...FIXED_DOCS.map((key) => ({ key })), ...COND_DOCS].flatMap((m) => {
    const it = docs[m.key] || {};
    if (m.textField && it.required !== "yes") return [];
    if (it.notPlanned || !it.plannedDate) return [];
    const extra = m.textField ? String(it[m.textField] || "").trim() : "";
    const title = DOC_LABELS[m.key][lang] + (extra ? ` — ${extra}` : "");
    const note = it.submitted ? `${u.statusSubmitted}${it.actualDate ? ` (${it.actualDate})` : ""}` : u.planned;
    return [{ id: `doc-${m.key}`, date: it.plannedDate, title: setBi("", lang, title), note: setBi("", lang, note), document: true, submitted: !!it.submitted }];
  });

  const items = [...system, ...(d.custom || []).map((c) => ({ ...c, system: false })), ...docItems]
    .map((x) => ({ ...x, days: daysUntil(x.date) })).sort((a, b) => a.date.localeCompare(b.date));

  const addCustom = () => {
    if (!draft.title.trim() || !draft.date) return toast.error(u.need);
    set({ custom: [...(d.custom || []), { id: `${Date.now()}`, title: setBi("", lang, draft.title.trim()), date: draft.date, note: setBi("", lang, draft.note.trim()) }] });
    setDraft({ title: "", date: "", note: "" });
  };

  const sendNow = async () => {
    setSending(true);
    try { const { data } = await api.post(`/cvs/${cvId}/deadlines/remind-now`); toast.success(`${u.sent} ${data.to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Email error"); }
    finally { setSending(false); }
  };

  const tone = (days) => days < 0 ? "text-[#94a3b8]" : days <= 7 ? "text-[#c0392b]" : days <= 30 ? "text-amber-600" : "text-[#1a2e4a]";

  const renderDocFields = (key) => {
    const it = docs[key] || {};
    return (
      <>
        <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-center">
          <div>
            <span className="text-[11px] text-[#64748b]">{u.planned}</span>
            <Input data-testid={`doc-${key}-planned`} type="date" value={it.plannedDate || ""} disabled={!!it.notPlanned} onChange={(e) => setDoc(key, { plannedDate: e.target.value })} className="bg-white mt-0.5" />
          </div>
          <label className="flex items-center gap-2 text-[12px] text-[#64748b] cursor-pointer whitespace-nowrap sm:mt-4">
            <Checkbox data-testid={`doc-${key}-notplanned`} checked={!!it.notPlanned} onCheckedChange={(v) => setDoc(key, { notPlanned: !!v, plannedDate: v ? "" : it.plannedDate })} /> {u.notPlanned}
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-[12px] text-[#1a2e4a] cursor-pointer">
            <Checkbox data-testid={`doc-${key}-submitted`} checked={!!it.submitted} onCheckedChange={(v) => setDoc(key, { submitted: !!v })} /> {u.submitted}
          </label>
          {it.submitted && (
            <div className="flex items-center gap-2">
              <span className="text-[12px] text-[#64748b]">{u.actualDate}:</span>
              <Input data-testid={`doc-${key}-actual`} type="date" value={it.actualDate || ""} onChange={(e) => setDoc(key, { actualDate: e.target.value })} className="bg-white h-9 w-[160px]" />
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-[#64748b] leading-relaxed">{u.intro}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-[13px] text-[#1a2e4a]">{u.country}</Label>
          <select data-testid="deadline-country" value={d.country || ""} onChange={(e) => set({ country: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
            <option value="">{u.pick}</option>
            {countries.map((c) => <option key={c.key} value={c.key}>{c.label[lang]}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[13px] text-[#1a2e4a]">{u.year}</Label>
          <select data-testid="deadline-year" value={d.year || ""} onChange={(e) => set({ year: e.target.value ? Number(e.target.value) : null })} className="mt-1 w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
            <option value="">—</option>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {items.length === 0 ? (
        <div data-testid="deadline-empty" className="text-sm text-[#94a3b8] italic border border-dashed border-[#cbd5e1] rounded-lg p-6 text-center">{u.none}</div>
      ) : (
        <ol data-testid="deadline-timeline" className="relative border-l-2 border-[#e2e8f0] ml-3 space-y-4">
          {items.map((x) => (
            <li key={x.id} data-testid={`deadline-item-${x.id}`} className="ml-5 relative">
              <span className={`absolute -left-[27px] top-1.5 h-3.5 w-3.5 rounded-full border-2 border-white ${x.days < 0 ? "bg-[#cbd5e1]" : x.days <= 7 ? "bg-[#c0392b]" : x.days <= 30 ? "bg-amber-500" : "bg-[#1a2e4a]"}`} />
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className={`font-mono text-[13px] font-bold ${tone(x.days)}`}>{x.date}</span>
                <span className={`text-[11px] font-semibold ${tone(x.days)}`}>
                  {x.days === 0 ? u.today : x.days < 0 ? `${Math.abs(x.days)} ${lang === "tr" ? "gün önce" : "days ago"} · ${u.passed}` : `${x.days} ${u.daysLeft}`}
                </span>
                <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${x.document ? "bg-[#e0e7ff] text-[#1a2e4a]" : x.system ? "bg-[#eef2f8] text-[#1a2e4a]" : "bg-[#fadbd8]/60 text-[#c0392b]"}`}>{x.document ? u.docBadge : x.system ? u.official : u.mine}</span>
                {x.document && x.submitted && <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-green-100 text-green-700 font-semibold">{u.statusSubmitted}</span>}
                {x.approximate && <span className="text-[10px] text-[#94a3b8]">({u.approx})</span>}
                {!x.system && !x.document && (
                  <button data-testid={`deadline-remove-${x.id}`} onClick={() => set({ custom: (d.custom || []).filter((c) => c.id !== x.id) })} className="ml-auto text-[#c0392b] hover:bg-[#fadbd8] rounded p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
              <div className="text-[14px] text-[#1a2e4a] font-semibold mt-0.5">{bi(x.title, lang)}</div>
              {bi(x.note, lang) && <div className="text-[12px] text-[#64748b]">{bi(x.note, lang)}</div>}
            </li>
          ))}
        </ol>
      )}

      <div data-testid="doc-planner" className="rounded-xl border border-[#1a2e4a]/20 bg-white p-4 space-y-4">
        <div className="text-[15px] font-bold text-[#1a2e4a] flex items-center gap-2"><FileCheck2 className="h-4 w-4 text-[#c0392b]" /> {u.docTitle}</div>
        <p className="text-[12.5px] text-[#64748b] leading-relaxed">{u.docIntro}</p>
        {FIXED_DOCS.map((key) => (
          <div key={key} data-testid={`doc-row-${key}`} className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-3 space-y-2">
            <div className="text-[13.5px] font-semibold text-[#1a2e4a]">{DOC_LABELS[key][lang]}</div>
            {renderDocFields(key)}
          </div>
        ))}
        {COND_DOCS.map((m) => {
          const it = docs[m.key] || {};
          return (
            <div key={m.key} data-testid={`doc-row-${m.key}`} className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-[13.5px] font-semibold text-[#1a2e4a]">{DOC_LABELS[m.key][lang]}</div>
                <div className="flex items-center gap-2">
                  <span className="text-[12px] text-[#64748b]">{u.requiredQ}</span>
                  <select data-testid={`doc-${m.key}-required`} value={it.required || ""} onChange={(e) => setDoc(m.key, { required: e.target.value })} className="h-9 rounded-md border border-input bg-white px-2 text-sm">
                    <option value="">{u.reqPick}</option>
                    <option value="yes">{u.reqYes}</option>
                    <option value="no">{u.reqNo}</option>
                  </select>
                </div>
              </div>
              {it.required === "yes" && (
                <div className="space-y-2 pt-1">
                  <Input data-testid={`doc-${m.key}-text`} value={it[m.textField] || ""} onChange={(e) => setDoc(m.key, { [m.textField]: e.target.value })} placeholder={m.key === "academicExams" ? u.examName : u.portfolioInfo} className="bg-white" />
                  {renderDocFields(m.key)}
                </div>
              )}
            </div>
          );
        })}
        <div className="flex gap-2 text-[12px] text-[#64748b] bg-[#eef2f8] rounded-lg p-3 leading-relaxed"><Info className="h-4 w-4 shrink-0 text-[#c0392b] mt-0.5" /> <span data-testid="doc-info-note">{u.docInfo}</span></div>
      </div>

      <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-4 space-y-3">
        <div className="text-[13px] font-bold text-[#1a2e4a] flex items-center gap-1.5"><Plus className="h-4 w-4 text-[#c0392b]" /> {u.custom}</div>
        <div className="grid sm:grid-cols-[1fr_170px] gap-2">
          <Input data-testid="custom-deadline-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder={u.customTitle} className="bg-white" />
          <Input data-testid="custom-deadline-date" type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} className="bg-white" />
        </div>
        <div className="flex gap-2">
          <Input data-testid="custom-deadline-note" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder={u.customNote} className="bg-white" />
          <Button data-testid="custom-deadline-add" onClick={addCustom} className="bg-[#1a2e4a] hover:bg-[#254066] text-white shrink-0"><Plus className="h-4 w-4 mr-1" /> {u.add}</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 justify-between rounded-xl border border-[#e2e8f0] p-4">
        <label className="flex items-center gap-2 text-sm text-[#1a2e4a] cursor-pointer">
          <Checkbox data-testid="deadline-email-toggle" checked={d.emailReminders !== false} onCheckedChange={(v) => set({ emailReminders: !!v })} />
          <Mail className="h-4 w-4 text-[#c0392b]" /> {u.email}
        </label>
        <Button data-testid="deadline-send-now" variant="outline" size="sm" disabled={sending || items.filter((x) => x.days >= 0).length === 0} onClick={sendNow} className="border-[#1a2e4a] text-[#1a2e4a]">
          {sending ? <Clock className="h-3.5 w-3.5 mr-1.5 animate-pulse" /> : <Mail className="h-3.5 w-3.5 mr-1.5" />} {u.test}
        </Button>
      </div>
      <div className="flex gap-2 text-[12px] text-[#64748b]"><AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" /> {u.warn}</div>
    </div>
  );
};
